export const enHeader = {
  product: "Product",
  company: "About",
};

export const enHero = {
  h1: "Building the Data Infrastructure for Physical AI.",
  h2: "Turning Human Skill Into Robot-Ready Data",
  p: "Actrium converts human demonstrations, robot rollouts, and simulation traces into training-ready datasets for embodied AI. NeuroScale, THOR capture hardware, AEgo Engine, and edge compute work together to make real-world physical skills measurable, learnable, and deployable.",
};

export const enBottleneck = {
  title: "The Physical AI Data Bottleneck",
  subtitle: "Robots Need More Than Video",
  diagnostic: "Diagnostic: Missing Physical Causality",
  lastCentimeter: "The Contact Blind Spot",
  lastCentimeterDesc:
    "Internet video can show what happened, but not how hands applied force, when objects slipped, or why a manipulation succeeded or failed. Actrium captures the hidden interaction signals that robot models need.",
  physicsGap: "The Sim-to-Real Gap",
  physicsGapDesc:
    "Contact, friction, wet surfaces, deformable objects, occlusion, and small assembly errors cannot be solved by simulation alone. AEgo and NeuroScale ground robot learning in real human physical experience.",
  systemReport: "SYSTEM_IMPACT_REPORT",
  occlusionRate: "Vision Occlusion Rate",
  failureRate: "Open-loop Failure Rate",
  reliability: "Deployable Skill Confidence",
};

export const enHowItWorks = {
  title: "How It Works",
  subtitle: "A closed-loop system for turning physical experience into deployable robot skills",
  step1: {
    title: "Capture: Human Demonstrations",
    desc: "THOR capture systems collect synchronized egocentric video, IMU, spatial context, and contact-rich demonstrations for Physical AI datasets.",
  },
  step2: {
    title: "Govern: AEgo Engine",
    desc: "The platform synchronizes, cleans, anonymizes, reconstructs 6D trajectories, segments action phases, detects contact events, scores quality, and exports training-ready formats.",
  },
  step3: {
    title: "Learn: NeuroScale + EGO-to-Robot",
    desc: "NeuroScale learns intent, force patterns, contact feedback, and task outcomes, then converts human demonstrations into robot-ready training data.",
  },
  step4: {
    title: "Deploy: Robot Feedback Loop",
    desc: "Real robot execution generates success and failure data that returns to the platform, continuously improving datasets, models, and deployment reliability.",
  },
};

export const enTafvla = {
  title: "Actrium Physical AI Data Infrastructure",
  subtitle:
    "NeuroScale, THOR capture hardware, AEgo Engine, edge compute, and real robot deployments in one closed-loop stack.",
};

export const enEcosystem = {
  title: "Closed-Loop Physical AI Ecosystem",
  subtitle:
    "Human demonstration, multimodal governance, foundation-model learning, robot deployment, and real-world feedback.",
};

export const enKitchenRobotics = {
  eyebrow: "Validation Scenario / Home Robotics",
  title: "Kitchen Robotics as a Physical AI Testbed.",
  subtitle:
    "A dual-arm kitchen setup for validating how Actrium data becomes practical, deployable robot behavior.",
  body: "The kitchen setup is a validation scenario for Physical AI data infrastructure: a complex, high-frequency environment with rich contact, heavy occlusion, wet surfaces, deformable objects, tools, and repeated daily tasks.",
  stats: [
    {
      value: "AEgo",
      label: "human kitchen demonstrations",
    },
    {
      value: "2",
      label: "arms for coordinated contact-rich tasks",
    },
    {
      value: "Edge",
      label: "local brain for private real-time learning",
    },
  ],
  capabilities: [
    {
      title: "Practical Kitchen Workflow",
      desc: "The first scope focuses on grocery unpacking, item recognition, sorting, produce washing, low-risk cutting, plating, dish collection, residue handling, and pre-rinsing.",
    },
    {
      title: "Local Physical AI Brain",
      desc: "High-performance edge compute supports low-latency perception, planning, safety checks, simulation, personalized learning, and failure recovery in privacy-sensitive homes.",
    },
    {
      title: "Human-to-Robot Skill Transfer",
      desc: "AEgo captures how people handle ingredients, tools, dishes, and clutter; NeuroScale and EGO-to-Robot convert those signals into robot-ready training data.",
    },
    {
      title: "Deployment Feedback Loop",
      desc: "Every robot run produces real success and failure examples, feeding AEgo Engine with the data needed to improve models, datasets, and future deployments.",
    },
  ],
  workflowTitle: "A Practical Kitchen Workflow",
  workflow: [
    {
      title: "Unpack",
      desc: "Identify grocery bags, open the workspace, and retrieve fruits, vegetables, packaged food, utensils, or waste.",
    },
    {
      title: "Sort & Prepare",
      desc: "Classify items and place them into zones for washing, storage, cutting, plating, or disposal.",
    },
    {
      title: "Wash & Handle",
      desc: "Rinse and drain produce, arrange ingredients, and prepare low-risk items with controlled motions and safety confirmation.",
    },
    {
      title: "Clean Up",
      desc: "Collect dishes, remove residue, pre-rinse, and place items into a dishwasher zone or drying area.",
    },
  ],
  brainTitle: "The Local Intelligence Behind the Kitchen Robot",
  brainDesc:
    "Physical AI data must be captured, preprocessed, structured, and evaluated close to the real-world environment. Edge compute is part of the data infrastructure, not a standalone hardware story.",
  brainPoints: [
    "Real-time perception, planning, and safety control",
    "Privacy-preserving processing for home kitchen video and behavior data",
    "Personal adaptation to household layouts, dishes, ingredients, and habits",
    "Daily organization of success and failure samples",
    "Optional privacy-filtered updates to the broader Actrium model network",
  ],
  roadmapTitle: "From Validation Demo to Pilot Deployment",
  roadmap: [
    {
      phase: "0-3 Months",
      title: "Platform + Demo Kickoff",
      desc: "THOR capture positioning, AEgo Engine Alpha, kitchen robot platform selection, and the first grocery-bag retrieval and sorting demo.",
    },
    {
      phase: "3-6 Months",
      title: "NeuroScale Validation",
      desc: "FFMG-to-hand-pose validation, grip-state validation, AEgo Engine synchronization and quality scoring, and a closed loop across unpacking, sorting, washing, and draining.",
    },
    {
      phase: "6-12 Months",
      title: "Edge Hub + High-End Pilot",
      desc: "AEgo Edge Hub validation, first EGO-to-Robot conversion, low-risk cutting, plating, dish handling, pre-rinsing, and one to two premium pilot sites.",
    },
    {
      phase: "12-24 Months",
      title: "Premium Pilot Product",
      desc: "Deploy in high-end homes, showrooms, service apartments, wellness communities, and flagship smart-kitchen environments.",
    },
  ],
  cta: "Request a Kitchen Robotics Demo",
};

export const enMoats = {
  title: "Why Actrium Is Hard to Replace",
  dataSupremacy: "Physical Data Assets",
  hardwareLockin: "THOR Capture Stack",
  temporalEdge: "NeuroScale Representation",
  reliabilityWall: "Application Feedback",
  dataSupremacyDesc:
    "Actrium produces training-ready datasets that include human motion, contact events, force-related FFMG signals, task context, and outcome labels.",
  hardwareLockinDesc:
    "THOR visual-inertial capture systems collect synchronized first-person and multi-view data that vision-only and robot-only collection miss.",
  temporalEdgeDesc:
    "NeuroScale unifies FFMG, egocentric video, hand trajectories, task text, and result labels into a model-ready representation of physical intent.",
  reliabilityWallDesc:
    "Kitchen and other robot deployments make Actrium more than a data vendor: they create a continuously improving Physical AI feedback system.",
};

export const enWhoWeServe = {
  title: "Who We Serve",
  subtitle: "For the teams building the next generation of Physical AI.",
  oem: "Robotics OEMs & Hardware Builders",
  labs: "Foundation Model & AI Labs",
  industrial: "Industrial Automation & Smart Manufacturing",
  research: "Research & Academia",
};

export const enCta = {
  title: "Build your next robotics model with real physical data.",
  subtitle:
    "Request access to THOR capture hardware, AEgo Engine, NeuroScale, Edge Hub processing, and our flagship kitchen robotics demo.",
  button: "Request Access",
};

export const enFooter = {
  bio: "Actrium AI builds Physical AI data infrastructure, converting human demonstrations, robot execution, and simulation traces into training-ready assets for embodied AI.",
  copyright: "© 2026 Impact Frontiers Inc. All rights reserved.",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
};

export const enProducts = {
  title: "THOR Vision Series",
  subtitle:
    "From lightweight first-person capture to synchronized multi-view collection, THOR supports the Physical AI data pipeline.",
  egoScale: {
    name: "THOR V2",
    subtitle: "Dual-Camera Visual-Inertial Capture",
    tagline: "Lightweight first-person demonstration capture",
    desc: "A head-mounted dual-camera system for environmental context, operator viewpoint, IMU, and synchronized egocentric video for Physical AI datasets.",
  },
  glove: {
    name: "THOR V4",
    subtitle: "Quad-Camera Visual-Inertial Capture",
    tagline: "Wide-angle synchronized multi-view capture",
    desc: "A four-camera hardware-synchronized system for wider visual coverage, teleoperation capture, and complex scene recording.",
  },
  armband: {
    name: "THOR V6",
    subtitle: "Six-Camera Visual-Inertial Capture",
    tagline: "Full-coverage multi-view data collection",
    desc: "A six-camera synchronized capture system for complex motion, mapping, full-body activity data, and multi-directional scene understanding.",
  },
};

export const translations = {
  header: enHeader,
  hero: enHero,
  bottleneck: enBottleneck,
  howItWorks: enHowItWorks,
  tafvla: enTafvla,
  ecosystem: enEcosystem,
  kitchenRobotics: enKitchenRobotics,
  moats: enMoats,
  whoWeServe: enWhoWeServe,
  cta: enCta,
  footer: enFooter,
  products: enProducts,
};
